/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useMemo, useState } from 'react';
import { Button, Stack } from '@mui/material';
import ResetButton from 'components/cmn/ResetButton';
import { useAppDispatch } from 'store';
import DialogContent from 'components/dialog/DialogContent';
import DialogTitle from 'components/dialog/DialogTitle';
import DialogBody from 'components/dialog/DialogBody';
import DialogButtons from 'components/dialog/DialogButtons';
import { closeDialog } from 'store/dialog/dialogSlice';
import { putUserSave } from 'store/project/projectSlice';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import DialogTextField from 'components/dialog/DialogTextField';
import isEmpty from 'lodash/isEmpty';
import { useProject } from 'hooks/useProject';
import { ProjectUserContent } from 'interface/project/projectInfo';
import { showMessage } from 'store/message/messageSlice';

interface IUserModifyDialogProps {
  projectId: string;
  info: ProjectUserContent;
  onReset: () => void;
}

function UserModifyDialog({ projectId, info, onReset }: IUserModifyDialogProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { getProjectUsersList } = useProject();
  const { mutate } = getProjectUsersList(projectId || '');
  const [userInfo, setUserInfo] = useState({
    userName: info.userName || '',
    userCompName: info.userCompName || '',
    userDept: info.userDept || '',
    userPosition: info.userPosition || '',
    userEmail: info.userEmail || '',
  });
  const isValid = useMemo((): boolean => {
    const requiredKeys: (keyof typeof userInfo)[] = [
      'userName',
      'userCompName',
      'userDept',
      'userPosition',
      'userEmail',
    ];
    return requiredKeys.every(key => !isEmpty(userInfo[key]));
  }, [userInfo]);

  const handleCancel = () => {
    dispatch(closeDialog());
  };
  const handleClick = async () => {
    dispatch(openBackdrop({}));
    dispatch(putUserSave({ projectId, userId: info.id, ...userInfo }))
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          dispatch(showMessage({ message: '정보가 수정되었습니다.', variant: 'success' }));
          mutate();
          onReset();
          handleCancel();
        } else {
          dispatch(showMessage({ message: response.data?.message || '수정에 실패하였습니다.', variant: 'warning' }));
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setUserInfo(prev => ({
      ...prev,
      [name]: value,
    }));
  };
  return (
    <DialogContent width={{ desktop: 450, mobile: '100vw' }} height={{ desktop: 'fit-content', mobile: 'fit-content' }}>
      <DialogTitle hideCloseButton>진단대상자 수정</DialogTitle>
      <DialogBody style={{ overflowX: 'hidden', overflowY: 'hidden !important' }}>
        <DialogTextField
          label="이름"
          text={userInfo.userName}
          variant="outlined"
          name="userName"
          onChange={handleChange}
        />
        <DialogTextField
          label="기업"
          text={userInfo.userCompName}
          variant="outlined"
          name="userCompName"
          onChange={handleChange}
        />
        <DialogTextField
          label="부서"
          text={userInfo.userDept}
          variant="outlined"
          name="userDept"
          onChange={handleChange}
        />
        <DialogTextField
          label="직급"
          text={userInfo.userPosition}
          variant="outlined"
          name="userPosition"
          onChange={handleChange}
        />
        <DialogTextField
          label="이메일"
          text={userInfo.userEmail}
          variant="outlined"
          name="userEmail"
          onChange={handleChange}
        />
      </DialogBody>
      <DialogButtons>
        <Stack direction="row" spacing={3.75}>
          <Button onClick={handleClick} variant="contained" sx={{ width: 100 }} disabled={isValid === false}>
            수정
          </Button>
          <ResetButton title="취소" onClick={handleCancel} sx={{ width: 100 }} />
        </Stack>
      </DialogButtons>
    </DialogContent>
  );
}

export default memo(UserModifyDialog);
